import { observable, action, runInAction, computed, makeObservable } from "mobx";
import { userInfoStore } from "Stores/UserInfoStore";
import { API } from "General/API";
import { BrowserStorage } from "utils/BrowserStorage";
import { API_URL } from "General/APIEndpoints";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { needsUnifiedAccountLinkPrompt } from "Pages/Login/utils/promptUnifiedAccountLinking";

const dataStore = {
  preferredLogin: "Login-Preferred",
  SAVE_USERNAME: "SAVE_USERNAME",
};

class LoginStore {
  /**
   * Login Settings
   */
  state: StoreState = "RAW";
  /**
   * Fields used in the Login Forms
   */
  userId: string = "";
  saveUserName: boolean = false;
  stayLoggedIn: boolean = false;
  password: string = "";

  /**
   * String that represents error message
   */
  errorMessage: string = "";

  constructor() {
    makeObservable(this, {
      state: observable,
      userId: observable,
      saveUserName: observable,
      stayLoggedIn: observable,
      password: observable,
      errorMessage: observable,
      clear: action.bound,
      setSaveUserName: action,
      setUserId: action.bound,
      setPassword: action.bound,
      submitUserId: computed,
      login: action.bound,
    });

    this.state = "RAW";
    this.userId = BrowserStorage.storePermanent(dataStore.SAVE_USERNAME) || "";
    this.saveUserName = this.userId !== "";
    this.password = "";
    this.stayLoggedIn = false;
    this.errorMessage = "";
  }

  clear() {
    this.state = "RAW";
    this.userId = BrowserStorage.storePermanent(dataStore.SAVE_USERNAME) || "";
    this.password = "";
    this.stayLoggedIn = false;
    this.errorMessage = "";
  }

  setSaveUserName(saveUserName: boolean) {
    if (!saveUserName) {
      BrowserStorage.storePermanent(dataStore.SAVE_USERNAME, null);
    }
    this.saveUserName = saveUserName;
  }

  setUserId(userId: string) {
    this.userId = userId;
    if (this.saveUserName) {
      BrowserStorage.storePermanent(dataStore.SAVE_USERNAME, userId);
    }
  }

  setPassword(password: string) {
    this.password = password;
  }

  get submitUserId() {
    if (window.GLOBAL.PRGROUP) {
      return `${this.userId}.${window.GLOBAL.PRGROUP}`;
    } else {
      return this.userId;
    }
  }

  @handleStoreState()
  async login(values: API_BasicLogin, skipLinkToUnifiedAccount?: boolean) {
    const formData = values;
    if (window.GLOBAL.PRGROUP) {
      formData.userId = `${values.userId}.${window.GLOBAL.PRGROUP}`;
    }
    if (Object.prototype.hasOwnProperty.call(values, "stayLoggedIn")) {
      this.stayLoggedIn = values.stayLoggedIn;
    }
    try {
      formData.stayLoggedIn = this.stayLoggedIn;
      this.errorMessage = "";
      const response = (await API.newData(API_URL.LOGIN.loginUserMain(), formData)) as API_LoginInformation;
      if (response.status === "MCP_WRITE_CONSENT") {
        runInAction(() => {
          userInfoStore.authenticate(response);
        });
        return response;
      }
      if (response.oauth2LoginRedirect) {
        window.open(response.oauth2LoginRedirect, "_self");
      }
      if (needsUnifiedAccountLinkPrompt(response) && !skipLinkToUnifiedAccount) {
        return response;
      }
      runInAction(() => {
        userInfoStore.authenticate(response);
      });
      if (response.status === "OK") {
        this.clear();
      }
      return undefined;
    } catch (err) {
      runInAction(() => {
        if (err.response) {
          switch (err.response.status) {
            case err.response.data && err.response.data.message:
              this.errorMessage = err.response.data.message;
              break;
            case 401:
              this.errorMessage = "Username and password do not match";
              break;
            case 409:
              this.errorMessage = "Password does not fit the requirements";
              break;
            case 403:
              this.errorMessage = "User is locked";
              throw err;
            default:
              this.errorMessage = "Error: Unknown!";
          }
        }
      });
      throw err;
    }
  }

  async completeMcpConsent(oauth2request: string, grantWrite: boolean) {
    const response = (await API.newData(API_URL.OAUTH2.postMcpConsent(), {
      oauth2request,
      grantWrite,
    })) as API_LoginInformation;
    if (response.oauth2LoginRedirect) {
      window.open(response.oauth2LoginRedirect, "_self");
    }
    return response;
  }
}

const loginStore = new LoginStore();

export { loginStore, LoginStore };
